import * as React from 'react';
import { useState } from 'react';
import { RoleType, MAX_INVITES_PER_ROLE } from '@promo/shared';
import { Button } from '@/components/ui/button';
import { useSendInvitations, useInvitations } from '../hooks/useInvitations';
import { toast } from '@/hooks/use-toast';
import { FormField } from '@/components/FormField';
import {
  isValidTebraEmail,
  splitEmailTokens,
  TokenizedEmailAutocomplete,
} from './TokenizedEmailAutocomplete';

// Type for the result of the sendInvitations API call
interface InvitationResult {
  success: { email: string; token?: string; acceptLink?: string }[];
  failed: { email: string; reason: string }[];
}

export interface InviteAssessorFormProps {
  packetId: string;
  onSuccess?: () => void;
}

export function InviteAssessorForm({ packetId, onSuccess }: InviteAssessorFormProps) {
  const [inviteeEmails, setInviteeEmails] = useState<string[]>([]);
  const [emailDraft, setEmailDraft] = useState('');
  const [invalidEmailTokens, setInvalidEmailTokens] = useState<string[]>([]);
  const [validationError, setValidationError] = useState('');

  // Existing invitations to enforce limits
  const { data: existingInvitations = [] } = useInvitations(packetId, RoleType.ASSESSOR);
  const { mutate: sendInvitations, isPending } = useSendInvitations();

  const collectEmailsForSubmit = (): { validEmails: string[]; invalidEmails: string[] } => {
    const draftTokens = splitEmailTokens(emailDraft);
    const validEmails: string[] = [];
    const invalidEmails = [...invalidEmailTokens];

    [...inviteeEmails, ...draftTokens].forEach(email => {
      const normalizedEmail = email.trim().toLowerCase();
      if (isValidTebraEmail(normalizedEmail)) {
        if (!validEmails.includes(normalizedEmail)) {
          validEmails.push(normalizedEmail);
        }
      } else {
        invalidEmails.push(email);
      }
    });

    return { validEmails, invalidEmails };
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError('');

    const { validEmails, invalidEmails } = collectEmailsForSubmit();

    if (invalidEmails.length > 0) {
      setValidationError(`Invalid email format or domain: ${invalidEmails.join(', ')}`);
      return;
    }

    if (validEmails.length === 0) {
      setValidationError('Please enter at least one valid email address.');
      return;
    }

    // Only enforce maximum limit - allow any number up to max
    const maxInvites = MAX_INVITES_PER_ROLE[RoleType.ASSESSOR];
    const totalInvitationCount = existingInvitations.length + validEmails.length;
    
    if (totalInvitationCount > maxInvites) {
      setValidationError(
        `You can have at most ${maxInvites} assessors. You're trying to invite ${validEmails.length} more, which would exceed the limit.`
      );
      return;
    }

    sendInvitations(
      {
        packetId,
        inviteeEmails: validEmails,
        roleToInvite: RoleType.ASSESSOR,
      },
      {
        onSuccess: (data: unknown) => {
          const result = data as InvitationResult;
          // Surface failures inline
          if (result.failed.length > 0) {
            const messages = result.failed.map(f => `${f.email}: ${f.reason}`);
            setValidationError(messages.join('; '));
          }
          // Show toast for any successful sends
          if (result.success.length > 0) {
            toast({
              title: 'Invitations sent',
              description: `Invitations sent to ${result.success.length} assessor${
                result.success.length > 1 ? 's' : ''
              }.`,
            });
            setInviteeEmails([]);
            setEmailDraft('');
            setInvalidEmailTokens([]);
            onSuccess?.();
          }
        },
        onError: (error) => {
          toast({
            title: 'Failed to send invitations',
            description: error instanceof Error ? error.message : 'An unknown error occurred',
            variant: 'destructive',
          });
        },
      }
    );
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="grid gap-4 py-4">
        <div className="grid grid-cols-4 gap-4">
          <FormField htmlFor="emails" label="Emails" className="col-span-4">
            <TokenizedEmailAutocomplete
              id="emails"
              value={inviteeEmails}
              draftValue={emailDraft}
              onChange={setInviteeEmails}
              onDraftChange={setEmailDraft}
              onInvalidTokensChange={setInvalidEmailTokens}
              placeholder="email1@tebra.com, email2@tebra.com"
              disabled={isPending}
            />
            {validationError && (
              <div className="text-sm text-destructive">
                {validationError}
              </div>
            )}
          </FormField>
        </div>
      </div>
      <div className="flex justify-end space-x-2">
        <Button type="submit" disabled={isPending}>
          {isPending ? 'Sending...' : 'Send Invitations'}
        </Button>
      </div>
    </form>
  );
}
